import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 1, ease: [0.76, 0, 0.24, 1], delay: 0.3 + i * 0.12 },
  }),
};

export const Hero = ({ cms }) => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const useCms = cms && lang === "en" && cms.hero_heading;
  const chunk = (str) => {
    const w = str.trim().split(/\s+/);
    const per = Math.ceil(w.length / 3);
    return [w.slice(0, per).join(" "), w.slice(per, per * 2).join(" "), w.slice(per * 2).join(" ")].filter(Boolean);
  };
  const lines = useCms ? chunk(cms.hero_heading) : [t("hero.l1"), t("hero.l2"), t("hero.l3")];
  const desc = useCms && cms.hero_description ? cms.hero_description : t("hero.desc");
  const bg = useCms && cms.hero_image ? cms.hero_image : "https://images.unsplash.com/photo-1699764681875-dd04ce36b1c3";
  const cta = useCms && cms.cta_primary ? cms.cta_primary : t("hero.cta");

  return (
    <section id="top" ref={ref} data-testid="hero-section"
      className="relative min-h-screen flex items-start lg:items-end overflow-hidden">
      <motion.div style={{ scale, y }} className="absolute inset-0 z-0">
        <img
          src={bg}
          alt="Marigold flowers"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-background/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-background/10" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-16 lg:pt-0 lg:pb-24 w-full">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] uppercase font-bold text-primary mb-5 sm:mb-6">
          {t("hero.overline")}
        </motion.p>

        <h1 className="font-serif font-medium text-foreground text-[11vw] sm:text-[9vw] lg:text-[8.5vw] leading-[0.95] lg:leading-[0.9] tracking-tighter">
          {lines.map((tx, i) => (
            <span key={i} className="reveal-line">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {tx}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3 }}
          className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-8 max-w-3xl">
          <p className="text-base text-muted-foreground leading-relaxed max-w-md">
            {desc}
          </p>
          <a href="#donate" data-testid="hero-donate-btn"
             className="shrink-0 rounded-full bg-primary text-primary-foreground px-8 py-4 text-sm font-semibold hover:scale-105 transition-transform inline-flex items-center gap-2 w-fit">
            {cta} <ArrowDown size={16} />
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
};
